import { useMemo, useState } from "react";
import {
  Modal,
  Upload,
  Button,
  Table,
  Typography,
  Alert,
  Space,
  Tag,
  Steps,
  InputNumber,
  Result,
  Tabs,
} from "antd";
import { InboxOutlined } from "@ant-design/icons";
import type { UploadFile } from "antd/es/upload/interface";
import type { ColumnsType } from "antd/es/table";
import { postApi } from "redux/apis";
import { toastText } from "utils/utils";
import { pageSizeOptionsPaging } from "utils/constant";

const { Dragger } = Upload;
const { Text, Title } = Typography;

const APP_BLUE = "#1677ff";
const APP_BLUE_BG = "#e6f4ff";
const EXCEL_GREEN = "#389e0d";
const EXCEL_GREEN_BG = "#f6ffed";

interface ReviewRow {
  key: string;
  sku: string;
  csvLabel: string;
  itemId: number | null;
  fullName: string | null;
  appFtz: number | null;
  appOnsite: number | null;
  excelFtz: number | null;
  excelOnsite: number | null;
  notPartOfFtzOnsite: boolean;
}

interface ReviewMismatchApiRow {
  sku: string;
  csvLabel?: string;
  itemId?: number | null;
  fullName?: string | null;
  currentFtzAvailable?: number | null;
  currentOnsiteAvailable?: number | null;
  csvFtzAvailable?: number | null;
  csvOnsiteAvailable?: number | null;
}

interface ReviewNotPartApiRow {
  sku: string;
  csvLabel?: string;
  csvFtzAvailable?: number | null;
  csvOnsiteAvailable?: number | null;
}

interface ReviewApiData {
  summary?: {
    unchanged?: number;
    matchedInDatabase?: number;
  };
  mismatchedQty?: ReviewMismatchApiRow[];
  similarQty?: ReviewMismatchApiRow[];
  notPartOfFtzOnsite?: ReviewNotPartApiRow[];
}

type ReviewTabKey = "diff" | "similar" | "notPart";

interface ApiErrorShape {
  response?: {
    data?: {
      message?: string;
    };
  };
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const steps = [
  { title: "Import", description: "Upload QBD report" },
  { title: "Review", description: "Edit & compare qty" },
];

function toNum(v: unknown): number | null {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function siteDiffers(a: number | null, b: number | null): boolean {
  if (a == null && b == null) return false;
  if (a == null || b == null) return true;
  return a !== b;
}

function itemName(row: ReviewRow): string {
  return (row.fullName || row.csvLabel || row.sku || "").trim();
}

function sortByItemName(list: ReviewRow[]): ReviewRow[] {
  return [...list].sort((a, b) =>
    itemName(a).localeCompare(itemName(b), undefined, {
      sensitivity: "base",
      numeric: true,
    })
  );
}

function formatDelta(app: number | null, excel: number | null): string {
  if (app == null && excel == null) return "—";
  if (app == null) return excel == null ? "—" : `→ ${excel}`;
  if (excel == null) return String(app);
  const d = excel - app;
  if (d === 0) return "0";
  return d > 0 ? `+${d}` : String(d);
}

const SiteQtyReviewModal = ({ isOpen, onClose, onSuccess }: Props) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [reviewing, setReviewing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [diffRows, setDiffRows] = useState<ReviewRow[]>([]);
  const [similarRows, setSimilarRows] = useState<ReviewRow[]>([]);
  const [notPartRows, setNotPartRows] = useState<ReviewRow[]>([]);
  const [activeReviewTab, setActiveReviewTab] = useState<ReviewTabKey>("diff");
  const [tablePagination, setTablePagination] = useState({
    current: 1,
    pageSize: 10,
  });

  const sortedDiffRows = useMemo(() => sortByItemName(diffRows), [diffRows]);
  const sortedSimilarRows = useMemo(
    () => sortByItemName(similarRows),
    [similarRows]
  );
  const sortedNotPartRows = useMemo(
    () => sortByItemName(notPartRows),
    [notPartRows]
  );

  const hasDiffs = diffRows.length > 0;

  const mapMatchedApiRow = (
    item: ReviewMismatchApiRow,
    index: number,
    prefix: string
  ): ReviewRow => ({
    key: `${prefix}-${item.itemId ?? item.sku}-${index}`,
    sku: item.sku,
    csvLabel: item.csvLabel || item.fullName || item.sku,
    itemId: item.itemId ?? null,
    fullName: item.fullName ?? null,
    appFtz: toNum(item.currentFtzAvailable),
    appOnsite: toNum(item.currentOnsiteAvailable),
    excelFtz: toNum(item.csvFtzAvailable),
    excelOnsite: toNum(item.csvOnsiteAvailable),
    notPartOfFtzOnsite: false,
  });

  const applyableCount = useMemo(
    () =>
      diffRows.filter(
        (r) =>
          r.itemId != null &&
          (siteDiffers(r.appFtz, r.excelFtz) ||
            siteDiffers(r.appOnsite, r.excelOnsite))
      ).length,
    [diffRows]
  );

  const resetState = () => {
    setCurrentStep(0);
    setFileList([]);
    setReviewing(false);
    setApplying(false);
    setDiffRows([]);
    setSimilarRows([]);
    setNotPartRows([]);
    setActiveReviewTab("diff");
    setTablePagination({ current: 1, pageSize: 10 });
  };

  const handleCancel = () => {
    resetState();
    onClose();
  };

  const updateDiffRow = (key: string, patch: Partial<ReviewRow>) => {
    setDiffRows((prev) =>
      prev.map((row) => (row.key === key ? { ...row, ...patch } : row))
    );
  };

  const handleReview = async () => {
    if (fileList.length === 0) {
      toastText("Please select a file", "error");
      return;
    }
    const file = (fileList[0].originFileObj || fileList[0]) as File;
    if (!file) {
      toastText("File object is missing", "error");
      return;
    }

    setReviewing(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await postApi("/items/review-site-qty", formData, true);
      const data = response?.data?.data as ReviewApiData;

      const mismatched: ReviewRow[] = (data?.mismatchedQty || []).map(
        (item, index) => mapMatchedApiRow(item, index, "mismatch")
      );

      const similar: ReviewRow[] = (data?.similarQty || []).map((item, index) =>
        mapMatchedApiRow(item, index, "similar")
      );

      const notPart: ReviewRow[] = (data?.notPartOfFtzOnsite || []).map(
        (item, index) => ({
          key: `notpart-${item.sku}-${index}`,
          sku: item.sku,
          csvLabel: item.csvLabel || item.sku,
          itemId: null,
          fullName: null,
          appFtz: null,
          appOnsite: null,
          excelFtz: toNum(item.csvFtzAvailable),
          excelOnsite: toNum(item.csvOnsiteAvailable),
          notPartOfFtzOnsite: true,
        })
      );

      setDiffRows(mismatched);
      setSimilarRows(similar);
      setNotPartRows(notPart);
      setActiveReviewTab("diff");
      setTablePagination({ current: 1, pageSize: 10 });
      setCurrentStep(1);
      toastText("Update QTY comparison completed", "success");
    } catch (error: unknown) {
      const err = error as ApiErrorShape;
      toastText(
        err?.response?.data?.message || "Failed to compare site quantity file",
        "error"
      );
    } finally {
      setReviewing(false);
    }
  };

  const handleApply = async () => {
    const items = diffRows
      .filter(
        (r) =>
          r.itemId != null &&
          (siteDiffers(r.appFtz, r.excelFtz) ||
            siteDiffers(r.appOnsite, r.excelOnsite))
      )
      .map((r) => ({
        itemId: r.itemId as number,
        sku: r.sku,
        ftzAvailable: r.excelFtz,
        onsiteAvailable: r.excelOnsite,
      }));

    if (items.length === 0) {
      toastText("No editable quantity differences to apply", "error");
      return;
    }

    setApplying(true);
    try {
      const response = await postApi("/items/apply-site-qty", { items });
      toastText(
        response?.data?.message || "Site quantities applied successfully",
        "success"
      );
      onSuccess?.();
      handleCancel();
    } catch (error: unknown) {
      const err = error as ApiErrorShape;
      toastText(
        err?.response?.data?.message || "Failed to apply site quantities",
        "error"
      );
    } finally {
      setApplying(false);
    }
  };

  const diffColumns: ColumnsType<ReviewRow> = [
    {
      title: "Item Name",
      key: "itemName",
      width: 260,
      fixed: "left",
      ellipsis: true,
      render: (_: unknown, record) => itemName(record) || "—",
    },
    {
      title: "3nStar App",
      onHeaderCell: () => ({
        style: { background: APP_BLUE_BG, color: APP_BLUE, fontWeight: 600 },
      }),
      children: [
        {
          title: "FTZ",
          dataIndex: "appFtz",
          key: "appFtz",
          width: 110,
          onHeaderCell: () => ({
            style: { background: APP_BLUE_BG, color: APP_BLUE },
          }),
          onCell: () => ({ style: { background: APP_BLUE_BG } }),
          render: (value: number | null) => value ?? "—",
        },
        {
          title: "Onsite",
          dataIndex: "appOnsite",
          key: "appOnsite",
          width: 110,
          onHeaderCell: () => ({
            style: { background: APP_BLUE_BG, color: APP_BLUE },
          }),
          onCell: () => ({ style: { background: APP_BLUE_BG } }),
          render: (value: number | null) => value ?? "—",
        },
      ],
    },
    {
      title: "Excel QBD Inventory",
      onHeaderCell: () => ({
        style: {
          background: EXCEL_GREEN_BG,
          color: EXCEL_GREEN,
          fontWeight: 600,
        },
      }),
      children: [
        {
          title: "FTZ",
          dataIndex: "excelFtz",
          key: "excelFtz",
          width: 110,
          onHeaderCell: () => ({
            style: { background: EXCEL_GREEN_BG, color: EXCEL_GREEN },
          }),
          onCell: () => ({ style: { background: EXCEL_GREEN_BG } }),
          render: (value: number | null, record) => (
            <InputNumber
              size="small"
              value={value ?? undefined}
              style={{ width: "100%" }}
              onChange={(v) =>
                updateDiffRow(record.key, {
                  excelFtz: v == null ? null : Number(v),
                })
              }
            />
          ),
        },
        {
          title: "Onsite",
          dataIndex: "excelOnsite",
          key: "excelOnsite",
          width: 110,
          onHeaderCell: () => ({
            style: { background: EXCEL_GREEN_BG, color: EXCEL_GREEN },
          }),
          onCell: () => ({ style: { background: EXCEL_GREEN_BG } }),
          render: (value: number | null, record) => (
            <InputNumber
              size="small"
              value={value ?? undefined}
              style={{ width: "100%" }}
              onChange={(v) =>
                updateDiffRow(record.key, {
                  excelOnsite: v == null ? null : Number(v),
                })
              }
            />
          ),
        },
      ],
    },
    {
      title: "Diff",
      key: "diff",
      width: 200,
      render: (_: unknown, record) => {
        const ftz = siteDiffers(record.appFtz, record.excelFtz);
        const onsite = siteDiffers(record.appOnsite, record.excelOnsite);
        return (
          <Space direction="vertical" size={0}>
            <Text>
              FTZ:{" "}
              <Text type={ftz ? "warning" : undefined} strong={ftz}>
                {formatDelta(record.appFtz, record.excelFtz)}
              </Text>
            </Text>
            <Text>
              Onsite:{" "}
              <Text type={onsite ? "warning" : undefined} strong={onsite}>
                {formatDelta(record.appOnsite, record.excelOnsite)}
              </Text>
            </Text>
          </Space>
        );
      },
    },
  ];

  const similarColumns: ColumnsType<ReviewRow> = [
    {
      title: "Item Name",
      key: "itemName",
      width: 260,
      fixed: "left",
      ellipsis: true,
      render: (_: unknown, record) => itemName(record) || "—",
    },
    {
      title: "3nStar App",
      onHeaderCell: () => ({
        style: { background: APP_BLUE_BG, color: APP_BLUE, fontWeight: 600 },
      }),
      children: [
        {
          title: "FTZ",
          dataIndex: "appFtz",
          key: "appFtz",
          width: 110,
          onHeaderCell: () => ({
            style: { background: APP_BLUE_BG, color: APP_BLUE },
          }),
          onCell: () => ({ style: { background: APP_BLUE_BG } }),
          render: (v: number | null) => v ?? "—",
        },
        {
          title: "Onsite",
          dataIndex: "appOnsite",
          key: "appOnsite",
          width: 110,
          onHeaderCell: () => ({
            style: { background: APP_BLUE_BG, color: APP_BLUE },
          }),
          onCell: () => ({ style: { background: APP_BLUE_BG } }),
          render: (v: number | null) => v ?? "—",
        },
      ],
    },
    {
      title: "Excel QBD Inventory",
      onHeaderCell: () => ({
        style: {
          background: EXCEL_GREEN_BG,
          color: EXCEL_GREEN,
          fontWeight: 600,
        },
      }),
      children: [
        {
          title: "FTZ",
          dataIndex: "excelFtz",
          key: "excelFtz",
          width: 110,
          onHeaderCell: () => ({
            style: { background: EXCEL_GREEN_BG, color: EXCEL_GREEN },
          }),
          onCell: () => ({ style: { background: EXCEL_GREEN_BG } }),
          render: (v: number | null) => v ?? "—",
        },
        {
          title: "Onsite",
          dataIndex: "excelOnsite",
          key: "excelOnsite",
          width: 110,
          onHeaderCell: () => ({
            style: { background: EXCEL_GREEN_BG, color: EXCEL_GREEN },
          }),
          onCell: () => ({ style: { background: EXCEL_GREEN_BG } }),
          render: (v: number | null) => v ?? "—",
        },
      ],
    },
    {
      title: "Status",
      key: "status",
      width: 140,
      render: () => <Tag color="success">Same qty</Tag>,
    },
  ];

  const notPartColumns: ColumnsType<ReviewRow> = [
    {
      title: "Item Name",
      key: "itemName",
      width: 260,
      ellipsis: true,
      render: (_: unknown, record) => itemName(record) || "—",
    },
    {
      title: "Excel FTZ",
      dataIndex: "excelFtz",
      key: "excelFtz",
      width: 120,
      render: (v: number | null) => v ?? "—",
    },
    {
      title: "Excel Onsite",
      dataIndex: "excelOnsite",
      key: "excelOnsite",
      width: 120,
      render: (v: number | null) => v ?? "—",
    },
    {
      title: "Status",
      key: "status",
      render: () => <Tag>Not part of FTZ and Onsite</Tag>,
    },
  ];

  const renderReviewTable = (
    dataSource: ReviewRow[],
    columns: ColumnsType<ReviewRow>,
    emptyText: string
  ) => {
    const total = dataSource.length;
    const pageSizeOptions = Array.from(
      new Set([
        ...pageSizeOptionsPaging.map(String),
        ...(total > 0 ? [String(total)] : []),
      ])
    );

    return (
      <Table
        size="small"
        rowKey="key"
        pagination={{
          current: tablePagination.current,
          pageSize: tablePagination.pageSize,
          total,
          pageSizeOptions,
          showSizeChanger: true,
          showTotal: (t) => `Total ${t} rows`,
          onChange: (page, pageSize) => {
            setTablePagination({
              current: page,
              pageSize: pageSize || 10,
            });
          },
        }}
        dataSource={dataSource}
        columns={columns}
        scroll={{ x: 950, y: 420 }}
        locale={{ emptyText }}
      />
    );
  };

  const renderStepContent = () => {
    if (currentStep === 0) {
      return (
        <>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="Upload QBD inventory report (CSV / Excel)"
            description="Uses the Available column from FTZ and Onsite sections. Next step shows quantity differences first."
          />
          <Dragger
            multiple={false}
            maxCount={1}
            accept=".csv,.xlsx,.xls"
            fileList={fileList}
            beforeUpload={(file) => {
              setFileList([file as UploadFile]);
              return false;
            }}
            onRemove={() => setFileList([])}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">Click or drag file to this area</p>
            <p className="ant-upload-hint">.csv, .xlsx, .xls — max 5 MB</p>
          </Dragger>
        </>
      );
    }

    if (currentStep === 1) {
      if (!hasDiffs) {
        const similarNote =
          similarRows.length > 0
            ? `${similarRows.length} matched item${similarRows.length === 1 ? "" : "s"} already have the same qty.`
            : "";
        const unmatchedNote =
          notPartRows.length > 0
            ? `${notPartRows.length} Excel row${notPartRows.length === 1 ? "" : "s"} did not match inventory (not shown).`
            : "";
        const subTitle =
          [similarNote, unmatchedNote].filter(Boolean).join(" ") ||
          "No quantity differences were found between Excel and 3nStar.";

        return (
          <Result status="info" title="No diff found" subTitle={subTitle} />
        );
      }

      return (
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Alert
            type="success"
            showIcon
            message={`Differences: ${diffRows.length} · Similar ones: ${similarRows.length} · Not part of FTZ/Onsite: ${notPartRows.length}`}
          />
          <div>
            <Title level={5} style={{ marginBottom: 4 }}>
              Update quantities
            </Title>
            <Text type="secondary">
              Apply writes Excel values into 3nStar for difference rows only (
              {applyableCount} row{applyableCount === 1 ? "" : "s"}).
            </Text>
          </div>
          <Tabs
            activeKey={activeReviewTab}
            onChange={(key) => {
              if (key === "similar" || key === "notPart" || key === "diff") {
                setActiveReviewTab(key);
                setTablePagination((prev) => ({ ...prev, current: 1 }));
              }
            }}
            items={[
              {
                key: "diff",
                label: `Differences (${diffRows.length})`,
                children: renderReviewTable(
                  sortedDiffRows,
                  diffColumns,
                  "No quantity differences"
                ),
              },
              {
                key: "similar",
                label: `Similar ones (${similarRows.length})`,
                children: renderReviewTable(
                  sortedSimilarRows,
                  similarColumns,
                  "No similar items"
                ),
              },
              {
                key: "notPart",
                label: `Not part of FTZ/Onsite (${notPartRows.length})`,
                children: renderReviewTable(
                  sortedNotPartRows,
                  notPartColumns,
                  "No unmatched items"
                ),
              },
            ]}
          />
        </Space>
      );
    }

    return null;
  };

  return (
    <Modal
      title={
        <div>
          <div style={{ fontSize: 18, fontWeight: 600 }}>
            Update QTY (FTZ / Onsite)
          </div>
          <div style={{ fontSize: 12, color: "#8c8c8c", marginTop: 4 }}>
            Import the QBD report, review differences, then apply Excel values
            into 3nStar.
          </div>
        </div>
      }
      open={isOpen}
      onCancel={handleCancel}
      centered
      destroyOnClose
      maskClosable={false}
      width={1200}
      footer={null}
    >
      <Steps
        current={currentStep}
        items={steps}
        style={{ marginBottom: 24 }}
      />

      <div style={{ minHeight: 360, marginBottom: 24 }}>
        {renderStepContent()}
      </div>

      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <div>
          {currentStep === 1 && (
            <Button
              onClick={() => {
                setCurrentStep(0);
                setDiffRows([]);
                setSimilarRows([]);
                setNotPartRows([]);
                setActiveReviewTab("diff");
                setTablePagination({ current: 1, pageSize: 10 });
              }}
              size="large"
            >
              Back
            </Button>
          )}
        </div>
        <Space>
          {currentStep === 1 && !hasDiffs ? (
            <Button type="primary" onClick={handleCancel} size="large">
              Close
            </Button>
          ) : (
            <>
              <Button onClick={handleCancel} size="large">
                Cancel
              </Button>
              {currentStep === 0 ? (
                <Button
                  type="primary"
                  size="large"
                  loading={reviewing}
                  disabled={fileList.length === 0}
                  onClick={handleReview}
                >
                  Next · Review
                </Button>
              ) : (
                <Button
                  type="primary"
                  size="large"
                  loading={applying}
                  disabled={applyableCount === 0}
                  onClick={handleApply}
                >
                  Apply ({applyableCount})
                </Button>
              )}
            </>
          )}
        </Space>
      </div>
    </Modal>
  );
};

export default SiteQtyReviewModal;
